import { errorMessage, isApiError, problemKey } from '@/shared/api';

import { isOrderStatusCode, type OrderStatusCode } from './model';

/**
 * Wat er misging bij een schrijfactie op een keuring, in de termen van de
 * schermen. De zinnen zelf staan in de `copy.ts` van het scherm; hier staat
 * alleen wát er mis is. Drie problemen verdienen een eigen zin, omdat de
 * gebruiker er iets mee moet:
 *
 * - **409 `invalid-status-transition`**: iemand anders zette de keuring net een
 *   stap verder. De API zegt waar hij nu staat en wat nog kan.
 * - **402 `insufficient-credits`**: de keuringen zijn op; er hoort een weg naar
 *   meer keuringen bij. Met een abonnement (`subscription`) zijn de keuringen van
 *   deze maand op en is dat extra keuringen kopen of een groter abonnement; zonder
 *   abonnement en in de proef (`trial`, de gratis keuringen zijn gebruikt) een
 *   abonnement of een losse rapportage.
 * - **409 `order-already-invoiced`**: de factuur staat al klaar in Facturatie.
 * - **409 `order-not-startable`**: de keuring kan nu niet gestart worden. De `reason`
 *   zegt waarom, zodat het scherm de knop kan aanpassen (overnemen aanbieden) in plaats
 *   van alleen een zin te tonen.
 *
 * Al het andere krijgt de melding van de API (bij een 422 de eerste veldfout).
 */
export type OrderProblem =
    | { kind: 'transition'; current: OrderStatusCode | null; allowed: OrderStatusCode[] }
    | {
          kind: 'credits';
          required: number | null;
          available: number | null;
          trial: boolean;
          subscription: boolean;
      }
    | { kind: 'invoiced' }
    | { kind: 'notStartable'; reason: StartRefusal; inspector: string | null; message: string }
    | { kind: 'other'; message: string };

/** Waarom een keuring niet te starten is, zoals `OrderNotStartable` het zegt. */
export const START_REFUSALS = [
    'status',
    'no_questionnaire',
    'in_progress_elsewhere',
    'assigned_to_other',
] as const;

export type StartRefusal = (typeof START_REFUSALS)[number] | 'unknown';

function refusalOrUnknown(value: unknown): StartRefusal {
    return typeof value === 'string' && (START_REFUSALS as readonly string[]).includes(value)
        ? (value as StartRefusal)
        : 'unknown';
}

function textOrNull(value: unknown): string | null {
    return typeof value === 'string' && value !== '' ? value : null;
}

function numberOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function statusOrNull(value: unknown): OrderStatusCode | null {
    return typeof value === 'string' && isOrderStatusCode(value) ? value : null;
}

export function describeOrderProblem(error: unknown): OrderProblem {
    if (!isApiError(error)) {
        return { kind: 'other', message: errorMessage(error) };
    }

    switch (problemKey(error)) {
        case 'invalid-status-transition':
            return {
                kind: 'transition',
                current: statusOrNull(error.problem.current_status),
                allowed: Array.isArray(error.problem.allowed_transitions)
                    ? error.problem.allowed_transitions
                          .map(statusOrNull)
                          .filter((status): status is OrderStatusCode => status !== null)
                    : [],
            };
        case 'insufficient-credits':
            return {
                kind: 'credits',
                required: numberOrNull(error.problem.credits_required),
                available: numberOrNull(error.problem.credits_available),
                trial: error.problem.trial === true,
                subscription: error.problem.trial !== true && error.problem.subscription === true,
            };
        case 'order-already-invoiced':
            return { kind: 'invoiced' };
        case 'order-not-startable':
            return {
                kind: 'notStartable',
                reason: refusalOrUnknown(error.problem.reason),
                inspector: textOrNull(error.problem.inspector_name),
                message: errorMessage(error),
            };
        default:
            return { kind: 'other', message: errorMessage(error) };
    }
}
